import crypto from "node:crypto";
import fs from "node:fs";
import path from "node:path";
import { buildTarget } from "../build-info";
import { manifestUrl } from "./check";
import { isTrustedUpdateUrl } from "./trusted-url";

/**
 * Self-update for standalone binaries: download the raw binary asset for this
 * build's target from the release manifest, verify its sha256, and atomically
 * rename it over the running executable (safe on macOS/Linux — the running
 * process keeps its open inode). The temp file is created next to the target
 * so the rename never crosses filesystems.
 */

export interface ManifestAsset {
	url: string;
	sha256: string;
}

export interface UpdateManifest {
	version: string;
	assets: Record<string, ManifestAsset>;
}

export class SelfUpdateError extends Error {
	constructor(
		public code: string,
		message: string,
	) {
		super(message);
	}
}

export async function fetchManifest(
	env: NodeJS.ProcessEnv = process.env,
): Promise<UpdateManifest> {
	const url = manifestUrl(env);
	if (!isTrustedUpdateUrl(url)) {
		throw new SelfUpdateError(
			"MANIFEST_UNTRUSTED",
			`Refusing to fetch the update manifest from an untrusted URL: ${url}.`,
		);
	}
	let response: Response;
	try {
		response = await fetch(url, {
			signal: AbortSignal.timeout(10_000),
			redirect: "follow",
		});
	} catch {
		throw new SelfUpdateError(
			"MANIFEST_UNREACHABLE",
			`Could not reach the update manifest at ${url}.`,
		);
	}
	if (!response.ok) {
		throw new SelfUpdateError(
			"MANIFEST_UNAVAILABLE",
			`Update manifest request failed with HTTP ${response.status}.`,
		);
	}
	let parsed: unknown;
	try {
		parsed = await response.json();
	} catch {
		throw new SelfUpdateError(
			"MANIFEST_INVALID",
			"Update manifest is not valid JSON.",
		);
	}
	const manifest = parsed as UpdateManifest;
	if (
		typeof manifest?.version !== "string" ||
		manifest.assets == null ||
		typeof manifest.assets !== "object"
	) {
		throw new SelfUpdateError(
			"MANIFEST_INVALID",
			"Update manifest is missing version or assets.",
		);
	}
	return manifest;
}

export function assetForThisBinary(
	manifest: UpdateManifest,
): ManifestAsset & { target: string } {
	if (!buildTarget) {
		throw new SelfUpdateError(
			"NOT_A_STANDALONE_BUILD",
			"This build has no compile target — self-update only applies to standalone binaries.",
		);
	}
	const asset = manifest.assets[buildTarget];
	if (!asset?.url || !asset.sha256) {
		throw new SelfUpdateError(
			"NO_ASSET_FOR_TARGET",
			`The latest release has no binary for ${buildTarget}.`,
		);
	}
	return { ...asset, target: buildTarget };
}

export async function downloadAndSwap(
	asset: ManifestAsset,
	execPath: string = process.execPath,
): Promise<void> {
	if (!isTrustedUpdateUrl(asset.url)) {
		throw new SelfUpdateError(
			"DOWNLOAD_UNTRUSTED",
			`Refusing to download the update binary from an untrusted URL: ${asset.url}.`,
		);
	}
	let response: Response;
	try {
		response = await fetch(asset.url, {
			signal: AbortSignal.timeout(120_000),
			redirect: "follow",
		});
	} catch {
		throw new SelfUpdateError(
			"DOWNLOAD_FAILED",
			`Could not download ${asset.url}.`,
		);
	}
	if (!response.ok) {
		throw new SelfUpdateError(
			"DOWNLOAD_FAILED",
			`Binary download failed with HTTP ${response.status}.`,
		);
	}
	const bytes = Buffer.from(await response.arrayBuffer());

	const digest = crypto.createHash("sha256").update(bytes).digest("hex");
	if (digest !== asset.sha256.toLowerCase()) {
		throw new SelfUpdateError(
			"CHECKSUM_MISMATCH",
			`Downloaded binary checksum ${digest} does not match the manifest — aborting.`,
		);
	}

	const dir = path.dirname(execPath);
	const tmp = path.join(dir, `.${path.basename(execPath)}.${process.pid}.new`);
	try {
		fs.writeFileSync(tmp, bytes, { mode: 0o755 });
		fs.renameSync(tmp, execPath);
	} catch (err) {
		try {
			fs.rmSync(tmp, { force: true });
		} catch {
			// best-effort cleanup
		}
		const code = (err as NodeJS.ErrnoException).code;
		if (code === "EACCES" || code === "EPERM" || code === "EROFS") {
			throw new SelfUpdateError(
				"PERMISSION_DENIED",
				`No write access to ${dir}. Re-run with elevated permissions (e.g. \`sudo whop upgrade\`) or reinstall.`,
			);
		}
		throw err;
	}
}
